"use client"
import { useEffect, useRef } from "react"
import gsap from "gsap"
import { SplitText } from "gsap/SplitText"
import Particles from "./Particles"
import { useScreenType } from "@/contexts/ScreenTypeContext"

export default function HeroHome() {
  const heroRef = useRef(null)
  const timelineRef = useRef<gsap.core.Timeline | null>(null)
  const isMobile = useScreenType()

  useEffect(
    () => {
      const hero = heroRef.current
      if (!hero) return

      const logo = hero.querySelector(".logo")
      const titleH1 = hero.querySelector("h1")
      const paragraph = hero.querySelector("p")
      const gsapHidden = gsap.utils.toArray(".gsap-hidden")

      // Clean previous timeline if exists
      if (timelineRef.current) {
        timelineRef.current.kill()
        timelineRef.current = null
      }

      // Split text once
      const titleSplit = new SplitText(titleH1, { type: "chars", mask: "chars" })
      const paragraphSplit = new SplitText(paragraph, { type: "lines", mask: "lines" })

      gsap.set(gsapHidden, {
        opacity: 0,
        visibility: "hidden"
      })

      gsap.set(logo, {
        opacity: 0,
        visibility: "hidden",
        yPercent: 100,
        scale: 0.75,
      })

      const tl = gsap.timeline({ defaults: { ease: "power3.out" } })

      timelineRef.current = tl



      // Animate h1 characters
      tl.to(gsapHidden, {
        opacity: 1,
        visibility: "visible",
        delay: 0.2,
        duration: 0.01 // just toggle visibility instantly
      }).from(titleSplit.chars, {
        y: 100,
        opacity: 0,
        stagger: 0.05,
        duration: 0.75
      })

      // Animate logo image
      tl.to(
        logo,
        {
          yPercent: 0,
          scale: 1.1,
          opacity: 1,
          duration: 1.5,
          rotation: 45,
          pointerEvents: "none",
          visibility: "visible"
        },
        "-=0.2"
      )

      // Animate paragraph lines
      tl.from(
        paragraphSplit.lines,
        {
          y: 50,
          opacity: 0,
          stagger: 0.05,
          duration: 0.5
        },
        "-=1.25"
      )

      return () => {
        // Kill timeline and revert splits to prevent memory leaks and duplicates
        if (timelineRef.current) {
          timelineRef.current.kill()
          timelineRef.current = null
        }
        titleSplit.revert()
        paragraphSplit.revert()
      }
    },
    []
  )

  return (
    <section
      ref={heroRef}
      style={{
        overflow: "hidden",
        margin: 0,
        padding: 0,
        height: "100vh",
        background: "url('/img/bg-hero.jpg') no-repeat",
        backgroundSize: "cover"
      }}
    >
      {/* Keep absolute here for canvas */}
      <div className="absolute top-1/2 -translate-y-1/2 p-[5vw] pointer-events-none gsap-hidden">
        <div className="grid lg:grid-cols-12 items-end gap-4 lg:gap-20">
          <h1 className="lg:col-span-8 text-white leading-none 2xl:!leading[0.9] flex flex-col">
            <div className="text-[16vw]">Rise</div>
            <div className="text-[6.75vw]">above the rest</div>
          </h1>

          <p className="lg:col-span-4 mt-4 text-base md:text-lg lg:text-xl text-white mb-3">
            We help you communicate better so you’re<span className="font-bold"> chosen</span>, not compared.
          </p>
        </div>
      </div>
      <Particles />
      <img src="/img/sun.svg" alt="Icon sunshine" className={`absolute lg:right-1/3 ${isMobile ? "size-36 top-20 right-12" : "size-52 top-16 right-4"} logo opacity-0`} />
    </section>
  )
}
